import { useId } from "react";
import { SPLASH_FILLS } from "@/lib/splashThemes";

const STRIPE = 300 / 13;
// Wavy top edge of the liquid that rises inside the letters.
const WAVE = `M0 18 ${Array.from({ length: 16 }, (_, i) => `Q${i * 125 + 62.5} ${i % 2 ? 36 : 0} ${(i + 1) * 125} 18`).join(" ")} V420 H0 Z`;
const WORD = { x: 500, y: 200, textAnchor: "middle", textLength: 930, lengthAdjust: "spacingAndGlyphs", className: "splash-word" };

const FlagFill = ({ id }) => (
  <>
    <pattern id={`${id}-stars`} width="34" height="26" patternUnits="userSpaceOnUse">
      <polygon points="9,3 10.8,8.4 16.4,8.4 11.8,11.7 13.6,17 9,13.8 4.4,17 6.2,11.7 1.6,8.4 7.2,8.4" fill="#fff" />
      <polygon points="26,16 27.8,21.4 33.4,21.4 28.8,24.7 30.6,30 26,26.8 21.4,30 23.2,24.7 18.6,21.4 24.2,21.4" fill="#fff" />
    </pattern>
    <pattern id={`${id}-fill`} width="1000" height="300" patternUnits="userSpaceOnUse">
      {Array.from({ length: 13 }, (_, i) => <rect key={i} y={i * STRIPE} width="1000" height={STRIPE + 0.5} fill={i % 2 ? "#ffffff" : "#B22234"} />)}
      <rect width="420" height={STRIPE * 7} fill="#3C3B6E" />
      <rect width="420" height={STRIPE * 7} fill={`url(#${id}-stars)`} />
      <animateTransform attributeName="patternTransform" type="translate" values="0 0; -18 0; 0 0" dur="3s" repeatCount="indefinite" />
    </pattern>
  </>
);

const GradientFill = ({ id, fill }) => {
  const [top, mid, bottom] = SPLASH_FILLS[fill];
  return (
    <>
      <linearGradient id={`${id}-fill`} x1="0" x2="0" y1="70" y2="215" gradientUnits="userSpaceOnUse">
        <stop offset="0" stopColor={top} /><stop offset="0.55" stopColor={mid} /><stop offset="1" stopColor={bottom} />
      </linearGradient>
      <pattern id={`${id}-glint`} width="90" height="90" patternUnits="userSpaceOnUse">
        <circle cx="12" cy="18" r="2.4" fill="#fff" /><circle cx="58" cy="44" r="1.6" fill="#fff" /><circle cx="34" cy="76" r="2" fill="#fff" /><circle cx="80" cy="8" r="1.2" fill="#fff" />
        <animateTransform attributeName="patternTransform" type="translate" values="0 0; 90 -90" dur="5s" repeatCount="indefinite" />
      </pattern>
      {fill === "candy" && (
        <pattern id={`${id}-candy`} width="56" height="56" patternUnits="userSpaceOnUse" patternTransform="rotate(35)">
          <rect width="22" height="56" fill="#C8102E" /><rect x="22" width="5" height="56" fill="#1F7A4D" />
          <animateTransform attributeName="patternTransform" type="translate" additive="sum" values="0 0; 56 0" dur="2.2s" repeatCount="indefinite" />
        </pattern>
      )}
    </>
  );
};

/** "757 REALTY" outline that draws itself, then fills with a themed liquid wave, glow and shine. */
export const SplashLetters = ({ theme }) => {
  const id = `sl${useId().replaceAll(":", "")}`;
  return (
    <svg viewBox="0 0 1000 300" className="splash-letters" role="img" aria-label="757 Realty" data-testid={`splash-letters-${theme.id}`}>
      <defs>
        {theme.fill === "flag" ? <FlagFill id={id} /> : <GradientFill id={id} fill={theme.fill} />}
        <linearGradient id={`${id}-shine`} x1="0" x2="1" y1="0" y2="0">
          <stop offset="0" stopColor="#fff" stopOpacity="0" /><stop offset="0.5" stopColor="#fff" stopOpacity="0.8" /><stop offset="1" stopColor="#fff" stopOpacity="0" />
        </linearGradient>
        <clipPath id={`${id}-rise`}>
          <path d={WAVE}><animateTransform attributeName="transform" type="translate" dur="1.7s" begin="0.9s" fill="freeze" values="0 300; -500 140; -1000 -40" keyTimes="0;0.55;1" /></path>
        </clipPath>
        <clipPath id={`${id}-text`}><text {...WORD}>757 REALTY</text></clipPath>
      </defs>
      <g className="splash-fill"><g clipPath={`url(#${id}-text)`}>
        <g clipPath={`url(#${id}-rise)`}>
          <rect width="1000" height="300" fill={`url(#${id}-fill)`} />
          {theme.fill === "candy" && <rect width="1000" height="300" fill={`url(#${id}-candy)`} opacity="0.92" />}
          {theme.fill !== "flag" && <rect width="1000" height="300" fill={`url(#${id}-glint)`} opacity="0.6" />}
        </g>
        <rect className="splash-shine" x="-300" width="260" height="300" fill={`url(#${id}-shine)`} />
      </g></g>
      <text {...WORD} className="splash-word splash-outline">757 REALTY</text>
    </svg>
  );
};
